import React, { useEffect, useState } from "react";
import { apiFetch } from "../../utils/apiFetch";

import "./global/global.css";

// Renders docs/zillow-listing-instructions.md as an in-app guide, one property
// at a time. Section slugs follow §9: each wizard step carries a stable slug so
// it can be addressed directly (URL-less anchors + stepper jump).
const WIZARD_STEPS = [
  { num: 1, slug: "property-info", title: "Property info" },
  { num: 2, slug: "rent-and-costs", title: "Rent & costs" },
  { num: 3, slug: "media", title: "Media" },
  { num: 4, slug: "screening-criteria", title: "Screening criteria" },
  { num: 5, slug: "description-and-amenities", title: "Description & amenities" },
  { num: 6, slug: "final-details", title: "Final details" },
  { num: 7, slug: "review-and-publish", title: "Review & publish" },
];

const ALL_SECTIONS = [
  "before-you-start",
  "gather-data",
  "sign-in",
  "create-or-open",
  ...WIZARD_STEPS.map((s) => s.slug),
];

// §6 — verify before you leave.
const FINAL_CHECKLIST = [
  "The property appears in Properties as Off-market",
  "Review page shows the correct rent, deposit, beds, baths and square footage",
  "Bathrooms and square footage match the unit data, not Zillow's public-records pre-fill",
  "Screening criteria match the resident-qualification string",
  "Every empty Zillow field has a reason (see the warning blocks above)",
  "Nothing was published",
];

const formatCurrency = (amount) => {
  if (!amount) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// §4 — Zillow only offers House, Townhome, Condo, or Apartment units.
const ZILLOW_PROPERTY_TYPES = {
  single_family: "House",
  townhouse: "Townhome",
  condo: "Condo",
  apartment: "Apartment units",
  multi_family: "Apartment units",
  duplex: "Apartment units",
  triplex: "Apartment units",
  adu: "House",
};

const zillowPropertyType = (type) =>
  ZILLOW_PROPERTY_TYPES[type] || type?.replace(/_/g, " ") || "N/A";

// §5 Step 5 — Zillow laundry choices (room-for-rent listings only offer
// shared choices; see the §7 trap).
const ZILLOW_LAUNDRY = {
  in_unit: "Washer-dryer included",
  machines_tenant_responsible: "Washer-dryer included — machines owned by tenant",
  shared: "Shared laundry in building",
  coin_op: "Coin-operated laundry in building",
  hookups: "(no direct equivalent — closest shared-laundry option)",
  none: "(leave unselected)",
};

const getLaundryDisplay = (type) =>
  ZILLOW_LAUNDRY[type] || type?.replace(/_/g, " ") || "(leave unselected)";

// §5 Step 4 — Zillow asks a plain Yes/No for pets.
const petsAllowedForZillow = (policy) => {
  if (!policy) return "No";
  return /^(no|not_allowed)/.test(policy) ? "No" : "Yes";
};

// Codes arrive as strings, but some amenity fields (private_outdoor_space,
// air_conditioning) are booleans in the model — pass those through untouched.
const formatAmenityName = (code) => {
  if (typeof code !== "string") return code;
  return code
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const nearestQuarter = (x) => Math.round(x * 4) / 4;

// §5 Step 4 — FastRealty states the ratio inverted ("rent to income ratio =
// 35%" means income-to-rent ≈ 2.86x). Zillow's dropdown steps in 0.25x.
const parseIncomeRatio = (quals) => {
  if (!quals) return null;
  const direct = quals.match(/income[\s-]*to[\s-]*rent[^0-9]*(\d+(?:\.\d+)?)\s*x?/i);
  if (direct) return { raw: direct[0], ratio: parseFloat(direct[1]) };
  const inverted = quals.match(/rent[\s-]*to[\s-]*income[^0-9]*(\d+(?:\.\d+)?)\s*%/i);
  if (inverted) {
    const pct = parseFloat(inverted[1]);
    if (pct > 0) return { raw: inverted[0], ratio: 100 / pct, inverted: true };
  }
  return null;
};

const parseCreditScore = (quals) => {
  if (!quals) return null;
  const m = quals.match(/credit[^0-9]*(\d{3,4})/i);
  return m ? m[1] : null;
};

const rentersInsuranceRequired = (quals) =>
  /renters?\s+insurance[^a-z]*required/i.test(quals || "");

// §7 — FastRealty states a single max-pets limit; Zillow counts per species.
const parseMaxPets = (restrictions) => {
  if (!restrictions) return null;
  const m = restrictions.match(/max(?:imum)?\s+pets?[^0-9]*(\d+)/i);
  return m ? parseInt(m[1], 10) : null;
};

// §7 — FastRealty can quote different rates per species; Zillow takes one
// monthly figure.
const parsePerSpeciesPetRent = (restrictions) => {
  if (!restrictions) return null;
  const dog = restrictions.match(/dogs?[^0-9$]*\$?(\d+)/i);
  const cat = restrictions.match(/cats?[^0-9$]*\$?(\d+)/i);
  if (!dog && !cat) return null;
  const parts = [];
  if (dog) parts.push(`dog $${dog[1]}/mo`);
  if (cat) parts.push(`cat $${cat[1]}/mo`);
  const higher = Math.max(
    ...(dog ? [parseInt(dog[1], 10)] : []),
    ...(cat ? [parseInt(cat[1], 10)] : [])
  );
  return { text: parts.join(", "), higher };
};

// §8 — what Zillow needs that this property's FastRealty record cannot supply.
const getGapItems = (property, unit, amenities, photos) => {
  const gaps = [
    "Listing contact email — FastRealty holds the owner's email; Zillow wants the listing contact's",
    "Roommate count and current pets on the property — not modelled",
    "Tour booking method, special offers, 3D tour — not modelled",
    "Per-category fees (administrative, parking, utilities) — not modelled as Zillow categories",
  ];
  if (!photos || photos.length === 0) {
    gaps.unshift("Photos — none on file in FastRealty; Zillow requires at least one to publish");
  }
  if (!unit?.availability_date && !property?.available_date) {
    gaps.push("Available date — N/A here; Zillow pre-fills its own, verify before Save & exit");
  }
  if (unit?.lease_duration_months == null) {
    gaps.push("Lease duration — N/A here; write the lease summary on Final details or flag it");
  }
  if (property?.year_built == null) {
    gaps.push("Year built — N/A here");
  }
  if (!amenities?.heating_type) {
    gaps.push("Heating type — N/A here; confirm with the owner");
  }
  if (!amenities?.air_conditioning && !amenities?.ac_type) {
    gaps.push("Cooling — air conditioning not recorded; confirm with the owner");
  }
  if (!unit?.minimum_resident_qualifications) {
    gaps.push("Screening criteria — no resident-qualification string on file");
  }
  return gaps;
};

const copyToClipboard = (text, done) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
  } else {
    fallbackCopy(text, done);
  }
};

const fallbackCopy = (text, done) => {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
  } catch (err) {
    // ignore; the button just won't have copied
  }
  document.body.removeChild(ta);
  done();
};

// --- Layout primitives -----------------------------------------------------

const rowStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  padding: "6px 0",
  borderBottom: "1px solid #f0f0f0",
};

const labelStyle = {
  width: "220px",
  color: "#666",
  fontSize: "13px",
  flexShrink: 0,
  paddingTop: "2px",
};

const valueStyle = {
  flex: 1,
  fontSize: "14px",
  color: "#333",
  fontWeight: 500,
  wordBreak: "break-word",
};

const noteStyle = {
  fontSize: "12px",
  color: "#888",
  fontWeight: 400,
  marginTop: "2px",
  lineHeight: 1.45,
};

const Text = ({ children, style }) => (
  <p style={{ fontSize: "13px", color: "#666", lineHeight: 1.5, marginBottom: "8px", ...style }}>
    {children}
  </p>
);

const SubHead = ({ children }) => (
  <p style={{ fontSize: "13px", fontWeight: 600, color: "#333", margin: "16px 0 8px" }}>
    {children}
  </p>
);

// §5 Step 3 — the photo content endpoint needs the JWT header, so <img src>
// cannot hit it directly. Fetch each photo as an authenticated blob, show a
// thumbnail, and hand the same object URL to a `download` anchor.
const PHOTO_EXT_BY_MIME = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/pjpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/tiff": "tif",
};

const PhotoDownloadCard = ({ photo, fileName }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [ext, setExt] = useState("jpg");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setBlobUrl(null);
    setFailed(false);
    apiFetch(`${process.env.REACT_APP_PAYVIEW_BASE_URL}${photo.photo_url}`)
      .then((response) => {
        if (!response.ok) throw new Error("Failed to load rental photo");
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setExt(PHOTO_EXT_BY_MIME[blob.type] || "jpg");
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photo.photo_url]);

  const caption =
    (photo.description || `Photo ${photo.display_order + 1}`) +
    (photo.is_primary ? " (primary)" : "");

  return (
    <div style={{ border: "1px solid #e0e0e0", borderRadius: "6px", backgroundColor: "#fff", overflow: "hidden" }}>
      {failed ? (
        <div
          role="img"
          aria-label="Photo unavailable"
          style={{
            alignItems: "center", background: "#eef3f1", color: "#6b7280",
            display: "flex", fontSize: "12px", height: "120px",
            justifyContent: "center", textAlign: "center",
          }}
        >
          Photo unavailable
        </div>
      ) : blobUrl ? (
        <img
          src={blobUrl}
          alt={caption}
          style={{ display: "block", width: "100%", height: "120px", objectFit: "cover" }}
        />
      ) : (
        <div aria-label="Loading rental property photo" style={{ background: "#eef3f1", height: "120px" }} />
      )}
      <div style={{ padding: "8px 10px" }}>
        <div style={{ fontSize: "12px", color: "#333", marginBottom: "6px", wordBreak: "break-word" }}>
          {caption}
        </div>
        {blobUrl ? (
          <a
            href={blobUrl}
            download={`${fileName}.${ext}`}
            style={{
              display: "inline-block", fontSize: "12px", fontWeight: 600,
              color: "#fff", backgroundColor: "#28a382", borderRadius: "4px",
              padding: "4px 10px", textDecoration: "none", cursor: "pointer",
            }}
          >
            Download
          </a>
        ) : (
          <span style={{ fontSize: "12px", color: "#999" }}>{failed ? "Not available" : "Loading..."}</span>
        )}
      </div>
    </div>
  );
};

// Field row with a copy button per value (§9).
const FieldRow = ({ label, value, note }) => {
  const [copied, setCopied] = useState(false);
  const hasValue = value !== null && value !== undefined && value !== "";
  const handleCopy = () => {
    if (!hasValue) return;
    copyToClipboard(String(value), () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <div style={rowStyle}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>
        {hasValue ? String(value) : "N/A"}
        {note && <div style={noteStyle}>{note}</div>}
      </div>
      <button
        onClick={handleCopy}
        disabled={!hasValue}
        title={hasValue ? "Copy value" : "Nothing to copy"}
        style={{
          flexShrink: 0,
          fontSize: "11px",
          padding: "3px 8px",
          border: "1px solid " + (copied ? "#28a382" : "#ccc"),
          borderRadius: "4px",
          backgroundColor: copied ? "#e8f5e9" : "#fff",
          color: copied ? "#2e7d32" : hasValue ? "#555" : "#bbb",
          cursor: hasValue ? "pointer" : "default",
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

// §7 warning blocks anchored to the steps they affect.
const Callout = ({ tone = "warn", title, children }) => {
  const tones = {
    warn: { bg: "#fff8e1", border: "#f5c542", color: "#8a6d00" },
    danger: { bg: "#fdecea", border: "#dc3545", color: "#a02622" },
    info: { bg: "#e3f2fd", border: "#1976d2", color: "#0d47a1" },
  };
  const t = tones[tone] || tones.warn;
  return (
    <div
      style={{
        backgroundColor: t.bg,
        borderLeft: `4px solid ${t.border}`,
        borderRadius: "4px",
        padding: "10px 14px",
        margin: "12px 0",
        textAlign: "left",
      }}
    >
      {title && (
        <div style={{ fontSize: "13px", fontWeight: 700, color: t.color, marginBottom: "4px" }}>
          {title}
        </div>
      )}
      <div style={{ fontSize: "13px", color: "#555", lineHeight: 1.5 }}>{children}</div>
    </div>
  );
};

// Expandable section with a stable slug id (§9).
const Section = ({ num, slug, title, open, onToggle, children }) => (
  <div
    id={slug}
    style={{
      backgroundColor: "#fff",
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      marginTop: "16px",
      scrollMarginTop: "16px",
    }}
  >
    <div
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 16px",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {num != null && (
        <span
          style={{
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            backgroundColor: "#28a382",
            color: "#fff",
            fontSize: "13px",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {num}
        </span>
      )}
      <span style={{ fontSize: "15px", fontWeight: 600, color: "#333", flex: 1 }}>{title}</span>
      <span style={{ fontSize: "11px", color: "#999" }}>{open ? "Collapse" : "Expand"}</span>
    </div>
    {open && <div style={{ padding: "0 16px 16px" }}>{children}</div>}
  </div>
);

const Chip = ({ bg, color, children }) => (
  <span
    style={{
      padding: "4px 8px",
      backgroundColor: bg,
      color,
      borderRadius: "4px",
      fontSize: "12px",
    }}
  >
    {children}
  </span>
);

// --- Page -------------------------------------------------------------------

const AdminZillowListing = () => {
  const [rentals, setRentals] = useState([]);
  const [selectedRental, setSelectedRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSections, setOpenSections] = useState(() =>
    Object.fromEntries(ALL_SECTIONS.map((slug) => [slug, true]))
  );
  const [checklist, setChecklist] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/internal-rentals/`
        );
        if (!response.ok) throw new Error("Failed to fetch rental properties");
        const data = await response.json();
        setRentals(data.rentals || []);
        if (data.rentals && data.rentals.length > 0) {
          setSelectedRental(data.rentals[0]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // The verify checklist is per draft; clear it when the selection changes.
  useEffect(() => {
    setChecklist({});
  }, [selectedRental?.property?.id]);

  const toggleSection = (slug) =>
    setOpenSections((prev) => ({ ...prev, [slug]: !prev[slug] }));

  const jumpToSection = (slug) => {
    setOpenSections((prev) => ({ ...prev, [slug]: true }));
    setTimeout(() => {
      document.getElementById(slug)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const toggleCheck = (index) =>
    setChecklist((prev) => ({ ...prev, [index]: !prev[index] }));

  if (loading) {
    return (
      <div className="client-list-root">
        <div className="client-list-container">
          <div className="client-list-heading" style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '16px' }}>
            <div className="lc">
              <h3 className="cl-rh">Zillow Listing</h3>
              <p className="cl-rh-sub">Zillow Rental Manager setup instructions for pending listings.</p>
            </div>
          </div>
          <div className="client-list-table">
            <p className="loading">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-list-root">
        <div className="client-list-container">
          <div className="client-list-heading" style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '16px' }}>
            <div className="lc">
              <h3 className="cl-rh">Zillow Listing</h3>
              <p className="cl-rh-sub">Zillow Rental Manager setup instructions for pending listings.</p>
            </div>
          </div>
          <div className="client-list-table">
            <p className="error">Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  const rental = selectedRental;
  const property = rental?.property;
  const unit = rental?.unit;
  const amenities = rental?.amenities;
  const utilities = rental?.utilities;

  const fullAddress = property
    ? `${property.address_line_1}${property.address_unit ? ` #${property.address_unit}` : ""}, ${property.address_city}, ${property.address_state} ${property.address_zip}`
    : "";

  // The API returns photos in display order (the model's Meta.ordering).
  const photos = rental?.photos || [];
  const addressSlug = property
    ? `${property.address_line_1}${property.address_unit ? ` unit ${property.address_unit}` : ""}`
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
    : "property";

  const quals = unit?.minimum_resident_qualifications || "";
  const incomeRatio = parseIncomeRatio(quals);
  const creditScore = parseCreditScore(quals);
  const maxPets = parseMaxPets(amenities?.pet_restrictions);
  const speciesRates = parsePerSpeciesPetRent(amenities?.pet_restrictions);
  const petsAnswer = petsAllowedForZillow(amenities?.pet_policy);
  const appliancesOn = [
    [amenities?.has_dishwasher, "Dishwasher"],
    [amenities?.has_refrigerator, "Refrigerator"],
    [amenities?.has_stove, "Stove"],
    [amenities?.has_microwave, "Microwave"],
  ]
    .filter(([on]) => on)
    .map(([, label]) => label);
  const amenityLabels = [
    ...(amenities?.amenities_list || []).map(formatAmenityName),
    ...(amenities?.custom_amenities || []),
  ];
  const utilityNames = utilities
    ? Object.entries({
        water: "Water",
        trash: "Trash",
        electricity: "Electricity",
        gas: "Gas",
        hoa: "HOA",
        sewer_septic: "Sewer/Septic",
        wifi_internet: "Wifi/Internet",
        cable_tv: "Cable/TV",
        lawn_care: "Lawn Care",
        pest_control: "Pest Control",
        pool_service: "Pool Service",
      })
        .filter(([key]) => utilities[key] === true)
        .map(([, name]) => name)
    : [];
  const availabilityDate = unit?.availability_date || property?.available_date;
  const allChecksDone = FINAL_CHECKLIST.every((_, i) => checklist[i]);

  return (
    <div className="client-list-root">
      <div className="client-list-container">
        <div className="client-list-heading" style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '16px' }}>
          <div className="lc">
            <h3 className="cl-rh">Zillow Listing</h3>
            <p className="cl-rh-sub">Zillow Rental Manager setup instructions for pending listings.</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0', height: 'calc(100vh - 200px)', marginTop: '20px' }}>
          {/* Left Panel - Property List */}
          <div style={{
            width: '35%',
            borderRight: '1px solid #e0e0e0',
            overflowY: 'auto',
            backgroundColor: '#fff',
            textAlign: 'left'
          }}>
            {rentals.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No rental properties found.
              </div>
            ) : (
              rentals.map((r) => (
                <div
                  key={r.property.id}
                  onClick={() => setSelectedRental(r)}
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    backgroundColor: selectedRental?.property.id === r.property.id ? '#e8f5e9' : '#fff',
                    borderLeft: selectedRental?.property.id === r.property.id ? '4px solid #28a382' : '4px solid transparent',
                  }}
                >
                  <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '4px', color: '#333' }}>
                    {r.property.address_line_1}
                    {r.property.address_unit && ` #${r.property.address_unit}`}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {r.property.address_city}, {r.property.address_state} {r.property.address_zip}
                  </div>
                  <div style={{ fontSize: '12px', color: '#28a382', marginTop: '4px', fontWeight: 500 }}>
                    {formatCurrency(r.unit?.monthly_rent)} / month
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Right Panel - Zillow Wizard Instructions */}
          <div style={{
            width: '65%',
            overflowY: 'auto',
            padding: '24px',
            backgroundColor: '#fafafa',
            textAlign: 'left'
          }}>
            {rental ? (
              <div>
                {/* Header: address + status chips (§9) */}
                <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px', color: '#333' }}>
                    Zillow Listing Setup Instructions
                  </h2>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>{fullAddress}</p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Chip bg="#e3f2fd" color="#1976d2">
                      {unit?.bedrooms || 0} bed / {unit?.bathrooms || 0} bath
                    </Chip>
                    <Chip bg="#e8f5e9" color="#2e7d32">
                      {formatCurrency(unit?.monthly_rent)}
                    </Chip>
                    <Chip bg="#fff8e1" color="#8a6d00">
                      Stop at Draft — do not Publish
                    </Chip>
                    <Chip bg="#f0f0f0" color="#555">
                      Off-market
                    </Chip>
                  </div>
                </div>

                {/* Progress: the seven wizard steps (§9) */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '16px 0' }}>
                  {WIZARD_STEPS.map((s) => (
                    <button
                      key={s.slug}
                      onClick={() => jumpToSection(s.slug)}
                      title={`#${s.slug}`}
                      style={{
                        fontSize: '12px',
                        padding: '5px 10px',
                        border: '1px solid #28a382',
                        borderRadius: '16px',
                        backgroundColor: '#fff',
                        color: '#28a382',
                        cursor: 'pointer',
                      }}
                    >
                      {s.num}. {s.title}
                    </button>
                  ))}
                </div>

                {/* Before you start (§1) */}
                <Section slug="before-you-start" title="Before you start" open={openSections['before-you-start']} onToggle={() => toggleSection('before-you-start')}>
                  <FieldRow label="FastRealty login" value="Google SSO with your FastRealty Google account" />
                  <FieldRow label="Zillow login" value="Email + password for your Zillow Rental Manager account, then an SMS code to the phone on file" />
                  <FieldRow label="Source data" value="FastRealty → Rental Manager → Properties → click the property row" />
                  <FieldRow label="At least one photo" value="Zillow requires one to publish — download the FastRealty photos in Step 3 (Media) below and upload them there" />
                  <Callout tone="info" title="Two facts decide whether this takes five minutes or thirty">
                    <p style={{ margin: '0 0 6px' }}>
                      <strong>The Zillow listing is a draft until you click Publish.</strong> "Save &amp; exit"
                      stores your work and the property stays Off-market. Nothing below puts a listing in
                      front of renters.
                    </p>
                    <p style={{ margin: 0 }}>
                      <strong>The wizard saves one step at a time, when you click Next.</strong> If you jump
                      between steps using the step tracker on the left, whatever you typed on the step you
                      left is discarded. Always finish a step with Next before moving.
                    </p>
                  </Callout>
                </Section>

                {/* Gather data (§2) */}
                <Section slug="gather-data" title="Gather the property data (FastRealty)" open={openSections['gather-data']} onToggle={() => toggleSection('gather-data')}>
                  <Text>1. Sign in at app.fastrealty.ai.</Text>
                  <Text>2. Go to Rental Manager → Properties and click the row for the property. It expands into a Property details panel.</Text>
                  <Text>3. The panel has two levels of data: Property information (address, type, year built, HOA, owner, description) and Units → Main unit (rent, beds, baths, square feet, deposit, fees, lease, utilities, amenities, pet policy, screening criteria).</Text>
                  <Text>4. Keep that panel open — Zillow's wizard asks for the same information in a different order.</Text>
                  <Callout tone="info" title="If Properties sits on “Loading...”">
                    Your FastRealty session has expired. Top-right menu → Log Out → Sign in with Google.
                    That clears it every time; it is not a server outage.
                  </Callout>
                </Section>

                {/* Sign in (§3) */}
                <Section slug="sign-in" title="Sign in to Zillow Rental Manager" open={openSections['sign-in']} onToggle={() => toggleSection('sign-in')}>
                  <Text>1. Go to www.zillow.com/rental-manager/ and sign in at www.zillow.com/user/acct/login/.</Text>
                  <Text>2. Enter the account email, then the password, then the 6-digit SMS code Zillow texts to the phone on file.</Text>
                  <Callout tone="info" title="Expect the SMS code twice">
                    The code arrives during sign-in and again at the phone-verification sub-step inside the
                    wizard (Final details, sub-step 3).
                  </Callout>
                </Section>

                {/* Create or open the property (§4) */}
                <Section slug="create-or-open" title="Create or open the property" open={openSections['create-or-open']} onToggle={() => toggleSection('create-or-open')}>
                  <Text>If the property is already in Zillow: find its row and use Actions → List. Otherwise, from Properties click Add property (top right) and fill:</Text>
                  <FieldRow
                    label="Street address"
                    value={property.address_line_1 + (property.address_unit ? ` #${property.address_unit}` : "")}
                    note="Type it, wait for the suggestion list (can take ten seconds), and click the USPS-validated address that matches."
                  />
                  <FieldRow label="City" value={property.address_city} />
                  <FieldRow label="State" value={property.address_state} />
                  <FieldRow label="Zip" value={property.address_zip} />
                  <FieldRow label="Property type" value={zillowPropertyType(property.property_type)} note="House, Townhome, Condo, or Apartment units." />
                  <Callout tone="danger" title="The address becomes permanent the moment you confirm">
                    You cannot edit it, add a unit number, or remove one afterwards. Validate before you
                    confirm. FastRealty stores addresses as typed and may hold a typo while Zillow matches
                    the USPS form exactly — compare street numbers rather than strings.
                  </Callout>
                  <Callout tone="danger" title='Leave “This is a room for rent with a shared living space” unchecked'>
                    Checking it changes the entire wizard: it asks "Who's listing the room for rent?",
                    room-and-shared-space descriptions, room square footage and roommate counts, and its
                    laundry options offer only shared choices with no "Washer-dryer included". The flag
                    cannot be unset — Edit property type shows that checkbox disabled. To fix it, archive
                    the property (it stays accessible under the Archived tab) and add it again with the box
                    unchecked.
                  </Callout>
                  <Callout tone="warn" title="Zillow may refuse the address">
                    If another partner already has an active listing at that location, Zillow returns
                    "Another partner has an active listing at this location" and Create listing does
                    nothing. There is no workaround in the UI; it needs Zillow support.
                  </Callout>
                  <Text style={{ marginTop: '8px' }}>Then click Create listing, then Confirm.</Text>
                </Section>

                {/* Step 1 — Property info (§5) */}
                <Section num={1} slug="property-info" title="Property info" open={openSections['property-info']} onToggle={() => toggleSection('property-info')}>
                  <FieldRow
                    label="Square footage"
                    value={unit?.square_feet}
                    note="Zillow pre-fills its own public-records figure — clear it and enter the FastRealty value."
                  />
                  <FieldRow label="Total bedrooms" value={unit?.bedrooms} />
                  <FieldRow label="Total bathrooms" value={unit?.bathrooms} />
                  <Callout tone="warn" title="Public-records disagreement (§7)">
                    Zillow pre-fills square footage, bedrooms and bathrooms from public records, which
                    frequently disagree with the unit data. The unit data wins — clear the pre-fill.
                  </Callout>
                  <Text style={{ marginTop: '8px' }}>→ Click Next to save this step and continue.</Text>
                </Section>

                {/* Step 2 — Rent & costs (§5) */}
                <Section num={2} slug="rent-and-costs" title="Rent & costs" open={openSections['rent-and-costs']} onToggle={() => toggleSection('rent-and-costs')}>
                  <FieldRow label="Monthly rent" value={formatCurrency(unit?.monthly_rent)} />
                  <FieldRow label="Security deposit" value={unit?.security_deposit ? formatCurrency(unit.security_deposit) : "(not set in FastRealty)"} />
                  <FieldRow label="Special offer" value="(leave blank)" note="Optional; FastRealty has no equivalent." />
                  <FieldRow
                    label="Fees"
                    value="(leave empty)"
                    note={`FastRealty has no matching Zillow categories; leave empty unless the lease says otherwise. For reference — application: ${formatCurrency(unit?.application_fee)}, admin: ${formatCurrency(unit?.admin_fee)}, parking: ${formatCurrency(unit?.parking_fee)}.`}
                  />
                  <FieldRow label="Show the total price on your listing" value="Toggle: leave off" note="Leave off until the fee structure is confirmed." />
                  <Text style={{ marginTop: '8px' }}>→ Click Next to save this step and continue.</Text>
                </Section>

                {/* Step 3 — Media (§5) */}
                <Section num={3} slug="media" title="Media" open={openSections['media']} onToggle={() => toggleSection('media')}>
                  <Text>Photos and an optional 3D tour.</Text>
                  {photos.length > 0 ? (
                    <>
                      <Text>
                        Download each FastRealty photo below, then upload the files on Zillow's
                        Media step (drag-and-drop or "Upload photos"). Primary first.
                      </Text>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                          gap: '10px',
                          margin: '8px 0 12px',
                        }}
                      >
                        {photos.map((photo, i) => (
                          <PhotoDownloadCard
                            key={photo.id}
                            photo={photo}
                            fileName={`${addressSlug}-photo-${i + 1}`}
                          />
                        ))}
                      </div>
                      <Callout tone="warn" title="This draft cannot be published without a photo (§8)">
                        FastRealty photos are not transferred to Zillow automatically — upload the
                        files you downloaded above before publishing, and flag it to whoever reviews
                        the draft, since publishing is out of scope here.
                      </Callout>
                    </>
                  ) : (
                    <Callout tone="warn" title="No photos on file for this property (§8)">
                      FastRealty holds no photos for this property, and Zillow requires at least one
                      to publish. Source photos another way and flag it to whoever reviews the
                      draft, since publishing is out of scope here.
                    </Callout>
                  )}
                  {unit?.video_url && <FieldRow label="Video / tour URL" value={unit.video_url} />}
                  <Text style={{ marginTop: '8px' }}>→ Click Next to save this step and continue.</Text>
                </Section>

                {/* Step 4 — Screening criteria (§5) */}
                <Section num={4} slug="screening-criteria" title="Screening criteria" open={openSections['screening-criteria']} onToggle={() => toggleSection('screening-criteria')}>
                  <FieldRow
                    label="Minimum income-to-rent ratio"
                    value={
                      incomeRatio
                        ? `Pick ${nearestQuarter(incomeRatio.ratio)}x`
                        : (quals || "(no qualification string on file)")
                    }
                    note={
                      incomeRatio
                        ? `FastRealty states this inverted — "${incomeRatio.raw}" ≈ ${incomeRatio.ratio.toFixed(2)}x income-to-rent. Zillow's dropdown steps in 0.25x; pick the nearest.`
                        : "Read it from FastRealty's RESIDENT QUALIFICATIONS below."
                    }
                  />
                  <FieldRow
                    label="Minimum credit score"
                    value={creditScore || "(read from qualifications)"}
                    note={'Example: "Credit 675+" → 675.'}
                  />
                  <FieldRow label="Are pets allowed?" value={petsAnswer} note={`FastRealty PET POLICY: ${amenities?.pet_policy || "not set"}.`} />
                  <FieldRow
                    label="Pet type counts"
                    value={amenities?.pet_restrictions || "(no restrictions on file)"}
                    note="Zillow counts per species (cats / small dogs under 40 lb / large dogs over 40 lb)."
                  />
                  <FieldRow label="Pet deposit" value={unit?.pet_deposit ? formatCurrency(unit.pet_deposit) : "(not set in FastRealty)"} />
                  <FieldRow
                    label="Pet rent"
                    value={
                      speciesRates
                        ? `$${speciesRates.higher}/mo (FastRealty quotes ${speciesRates.text})`
                        : (unit?.pet_fee ? `${formatCurrency(unit.pet_fee)}/mo` : "(not set in FastRealty)")
                    }
                    note={speciesRates ? "Zillow takes one monthly figure — use the higher rate or the rate for the species being advertised." : undefined}
                  />
                  <FieldRow label="Resident qualifications (source)" value={quals || "(none)"} />
                  <Callout tone="warn" title="Pet-count conflict (§7)">
                    FastRealty states a single limit{maxPets ? ` — "Max pets allowed: ${maxPets}"` : ""},
                    while Zillow counts per species. FastRealty's "1 dog" does not say what size. Confirm
                    with the owner before choosing.
                  </Callout>
                  {petsAnswer === "Yes" && (
                    <Callout tone="info" title="Selecting Yes reveals more fields">
                      Pet-type counters (cats, small dogs under 40 lb, large dogs over 40 lb), the deposit
                      and the monthly pet rent appear. You must add at least one pet type before the step
                      will advance.
                    </Callout>
                  )}
                  <Text style={{ marginTop: '8px' }}>→ Click Next to save this step and continue.</Text>
                </Section>

                {/* Step 5 — Description & amenities (§5) */}
                <Section num={5} slug="description-and-amenities" title="Description & amenities" open={openSections['description-and-amenities']} onToggle={() => toggleSection('description-and-amenities')}>
                  <FieldRow
                    label="Property description"
                    value={property?.description || "(none on file)"}
                    note="Extend it with the amenity list below."
                  />
                  <FieldRow
                    label="Append to description"
                    value={amenityLabels.length ? `Amenities: ${amenityLabels.join(", ")}` : "(no amenity list)"}
                  />
                  {utilityNames.length > 0 && (
                    <FieldRow label="Included utilities" value={utilityNames.join(", ")} note="Mention in the description — Zillow's fee categories stay empty." />
                  )}
                  <FieldRow
                    label="Laundry"
                    value={getLaundryDisplay(amenities?.laundry_type)}
                    note={`FastRealty LAUNDRY TYPE: ${amenities?.laundry_type || "not set"}.`}
                  />
                  <FieldRow
                    label="Cooling"
                    value={
                      amenities?.ac_type
                        ? formatAmenityName(amenities.ac_type)
                        : amenities?.air_conditioning === true
                          ? "Yes (type not recorded — pick the closest Cooling type)"
                          : amenities?.air_conditioning === false
                            ? "No — leave Cooling unselected"
                            : "(not recorded)"
                    }
                    note="FastRealty stores air conditioning as yes/no; ac_type refines it when set."
                  />
                  <FieldRow
                    label="Heating"
                    value={amenities?.heating_type ? formatAmenityName(amenities.heating_type) : "(N/A in FastRealty)"}
                    note="Often N/A — confirm with the owner."
                  />
                  <FieldRow
                    label="Appliances"
                    value={appliancesOn.length ? appliancesOn.join(", ") : "(none flagged)"}
                    note="From FastRealty HAS_DISHWASHER / HAS_REFRIGERATOR / HAS_STOVE / HAS_MICROWAVE."
                  />
                  <FieldRow
                    label="Flooring"
                    value={amenities?.flooring_type ? formatAmenityName(amenities.flooring_type) : "(not recorded)"}
                  />
                  <FieldRow
                    label="Parking"
                    value={amenities?.parking_type ? formatAmenityName(amenities.parking_type) : "(not recorded)"}
                    note={amenities?.parking_type === "garage" ? "FastRealty's plain \"garage\" does not say attached, detached or off-street — confirm with the owner." : undefined}
                  />
                  <FieldRow
                    label="Outdoor amenities"
                    value={amenities?.private_outdoor_space ? 'Yes — check "Balcony or deck"' : "(none recorded)"}
                    note="FastRealty flag covers balcony, patio, or yard — confirm which with the owner."
                  />
                  <FieldRow
                    label="Additional amenities"
                    value={amenityLabels.join(", ") || "(none)"}
                    note="Free-text field for anything in AMENITIES LIST that Zillow does not model."
                  />
                  <Text style={{ marginTop: '8px' }}>→ Click Next to save this step and continue.</Text>
                </Section>

                {/* Step 6 — Final details (§5): five sub-steps */}
                <Section num={6} slug="final-details" title="Final details (five sub-steps)" open={openSections['final-details']} onToggle={() => toggleSection('final-details')}>
                  <SubHead>Sub-step 1 — Lease terms &amp; insurance</SubHead>
                  <FieldRow
                    label="Lease terms"
                    value={unit?.lease_duration_months ? `${unit.lease_duration_months} months` : "(N/A — write the lease summary here, or leave blank and flag it)"}
                    note="FastRealty's LEASE DURATION is often N/A."
                  />
                  <FieldRow
                    label="Renters insurance"
                    value={rentersInsuranceRequired(quals) ? "Yes" : "(check RESIDENT QUALIFICATIONS)"}
                    note='"Renters insurance required" in the qualifications string → Yes.'
                  />
                  <SubHead>Sub-step 2 — Who is listing this property</SubHead>
                  <Text>Zillow flags this section as "Action needed" until you confirm the owner/manager identity.</Text>
                  <FieldRow label="Owner name" value={property?.property_owner_name || property?.landlord_name || "(not specified)"} />
                  <FieldRow label="Owner email" value={property?.property_owner_email || property?.landlord_email || "(not specified)"} note="FastRealty has the owner's email; Zillow wants the listing contact's — see the gaps panel." />
                  <FieldRow label="Owner phone" value={property?.property_owner_phone || property?.landlord_phone || "(not specified)"} />
                  <SubHead>Sub-step 3 — Verify your phone number</SubHead>
                  <Text>Second SMS code — the same code flow as sign-in.</Text>
                  <SubHead>Sub-step 4 — Tour booking method</SubHead>
                  <Text>Optional; leave unset to handle tour requests manually (not modelled in FastRealty).</Text>
                  <SubHead>Sub-step 5 — Confirm final details</SubHead>
                  <FieldRow
                    label="Date available"
                    value={availabilityDate || "(N/A here — Zillow pre-fills its own; verify)"}
                    note="Zillow pre-fills Date available and Lease duration from its own data — check both against the source."
                  />
                  <FieldRow
                    label="Lease duration"
                    value={unit?.lease_duration_months ? `${unit.lease_duration_months} months` : "(N/A here — verify Zillow's pre-fill)"}
                  />
                  <Text style={{ marginTop: '8px' }}>→ Complete each sub-step so the wizard saves it.</Text>
                </Section>

                {/* Step 7 — Review & publish (§5) */}
                <Section num={7} slug="review-and-publish" title="Review & publish" open={openSections['review-and-publish']} onToggle={() => toggleSection('review-and-publish')}>
                  <Text>
                    Read the Review page field by field against the FastRealty panel. Zillow groups
                    everything as Property information, Rent details, Media, Amenities, Screening criteria,
                    Costs and fees, Final details — and flags each section as complete or incomplete.
                  </Text>
                  <Callout tone="danger" title="Then click Save & exit — not Publish">
                    The listing is stored as a draft and the property remains Off-market. Do not click
                    Publish until the draft has been reviewed.
                  </Callout>
                </Section>

                {/* Verify before you leave (§6) */}
                <SubHead>Verify before you leave</SubHead>
                <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '12px 16px' }}>
                  {FINAL_CHECKLIST.map((item, i) => (
                    <label
                      key={item}
                      style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-start',
                        padding: '6px 0',
                        borderBottom: i < FINAL_CHECKLIST.length - 1 ? '1px solid #f0f0f0' : 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <input type="checkbox" checked={!!checklist[i]} onChange={() => toggleCheck(i)} style={{ marginTop: '2px' }} />
                      <span style={{ fontSize: '13px', color: checklist[i] ? '#888' : '#333', textDecoration: checklist[i] ? 'line-through' : 'none' }}>
                        {item}
                      </span>
                    </label>
                  ))}
                  {allChecksDone && (
                    <Text style={{ color: '#2e7d32', fontWeight: 600, marginTop: '10px', marginBottom: 0 }}>
                      All checks passed — the draft is ready for review. Still: do not Publish.
                    </Text>
                  )}
                </div>

                {/* Gaps panel (§8) */}
                <SubHead>Needs attention — data FastRealty does not supply</SubHead>
                <Callout tone="warn" title="Flag these to whoever reviews the draft rather than inventing values">
                  <ul style={{ margin: 0, paddingLeft: '18px' }}>
                    {getGapItems(property, unit, amenities, photos).map((gap) => (
                      <li key={gap} style={{ marginBottom: '4px' }}>{gap}</li>
                    ))}
                  </ul>
                </Callout>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                Select a property to view Zillow setup instructions
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminZillowListing;
